import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { FaArrowLeft, FaChevronDown, FaChevronUp, FaTimes, FaChevronLeft, FaChevronRight, FaExpand } from 'react-icons/fa';
import { getProductByIdAPI } from '../../utils/api';
import { BASE_URL } from '../../utils/configConstants';
import CommonButton from '../../components/CommonButton/CommonButton';
import './ProductDetails.css';

const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const source = searchParams.get('source') || 'product';
  const categoryId = searchParams.get('categoryId');
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedImage, setSelectedImage] = useState(0);

  // Only for description and return policy
  const [expandedSections, setExpandedSections] = useState({
    description: false,
    returnPolicy: false
  });

  // Image modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImageIndex, setModalImageIndex] = useState(0);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  useEffect(() => {
    if (product?.colors && product?.colors.length > 0) {
      const sortedColors = getSortedColors();
      setSelectedColor(sortedColors[0].name);
    }
  }, [product]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await getProductByIdAPI(id);
      if (response.status === 200) {
        setProduct(response.data);
      } else {
        setError('Failed to fetch product details');
      }
    } catch (err) {
      setError('Error fetching product details');
      console.error('Error fetching product:', err);
    } finally {
      setLoading(false);
    }
  };

  const getImagesByColor = (colorName) => {
    if (!product || !product.images) return [];
    return product.images.filter(img =>
      img?.color?.name === colorName ||
      img?.color_name === colorName
    );
  };

  const getCurrentImages = () => {
    if (selectedColor) {
      const colorImages = getImagesByColor(selectedColor);
      return colorImages.length > 0 ? colorImages : product?.images || [];
    }
    return product?.images || [];
  };

  const getSortedColors = () => {
    if (!product?.colors) return [];
    return product?.colors?.sort((a, b) => {
      const aHasImages = getImagesByColor(a.name).length > 0;
      const bHasImages = getImagesByColor(b.name).length > 0;

      // Colors with images come first
      if (aHasImages && !bHasImages) return -1;
      if (!aHasImages && bHasImages) return 1;

      return 0;
    });
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const CollapsibleSection = ({ title, content, sectionKey, isExpanded, onToggle }) => {
    if (!content || content === 'N/A') return null;

    return (
      <div className="collapsible-section">
        <button
          className="collapsible-header"
          onClick={() => onToggle(sectionKey)}
        >
          <span className="collapsible-title">{title}</span>
          {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
        </button>
        <div className={`collapsible-content ${isExpanded ? 'expanded' : ''}`}>
          <div
            dangerouslySetInnerHTML={{ __html: content }} className="collapsible-text"
          />
        </div>
      </div>
    );
  };

  // Image modal functions
  const openImageModal = (imageIndex) => {
    setModalImageIndex(imageIndex);
    setIsModalOpen(true);
  };

  const closeImageModal = () => {
    setIsModalOpen(false);
  };

  const nextImage = () => {
    const currentImages = getCurrentImages();
    setModalImageIndex((prev) => (prev + 1) % currentImages.length);
  };

  const prevImage = () => {
    const currentImages = getCurrentImages();
    setModalImageIndex((prev) => (prev - 1 + currentImages.length) % currentImages.length);
  };

  const handleKeyDown = (e) => {
    if (!isModalOpen) return;

    if (e.key === 'Escape') {
      closeImageModal();
    } else if (e.key === 'ArrowRight') {
      nextImage();
    } else if (e.key === 'ArrowLeft') {
      prevImage();
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isModalOpen]);

  if (loading) {
    return (
      <div className="product-details-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading product details...</p>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="product-details-container">
        <div className="error-state">
          <p>Error: {error || 'Product not found'}</p>
          <div className="order-details-header">
            <button className="back-button" onClick={() => {
              const backUrl = source === 'category' && categoryId
                ? `/product?categoryId=${categoryId}`
                : '/product';
              navigate(backUrl);
            }}>
              <FaArrowLeft /> Back to Product
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentImages = getCurrentImages();

  return (
    <div className="product-details-container">
      {/* Header */}
      <div className="order-details-header">
        <button className="back-button" onClick={() => {
          const backUrl = source === 'category' && categoryId
            ? `/product?categoryId=${categoryId}`
            : '/product';
          navigate(backUrl);
        }}>
          <FaArrowLeft /> Back to Product
        </button>
      </div>

      {/* Main Content */}
      <div className="product-content">
        {/* Product Images */}
        <div className="product-images">
          <div className="main-image" onClick={() => openImageModal(selectedImage)}>
            {currentImages.length > 0 ? (
              <img
                src={`${BASE_URL}${currentImages[selectedImage]?.image}`}
                alt={product.name}
                className="product-image"
                onError={(e) => {
                  console.error('Image failed to load:', e.target.src);
                  e.target.style.display = 'none';
                  const placeholder = e.target.nextElementSibling;
                  if (placeholder) {
                    placeholder.style.display = 'flex';
                  }
                }}
              />
            ) : null}
            <div className="no-image-placeholder" style={{ display: currentImages.length === 0 ? 'flex' : 'none' }}>
              <p>No image available</p>
            </div>
            {/* Click indicator */}
            <div className="image-click-indicator">
              <FaExpand className="enlarge-icon" />
            </div>
          </div>

          {/* Thumbnails */}
          {currentImages.length > 1 && (
            <div className="image-thumbnails-container">
              <div className="image-thumbnails">
                {currentImages.map((image, index) => (
                  <img
                    key={index}
                    src={`${BASE_URL}${image.image}`}
                    alt={`${product.name} - ${index + 1}`}
                    className={`thumbnail ${selectedImage === index ? 'active' : ''}`}
                    onClick={() => setSelectedImage(index)}
                    onError={(e) => {
                      console.error('Thumbnail failed to load:', e.target.src);
                      e.target.style.display = 'none';
                    }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Product Information */}
        <div className="product-info">
          <h2 className="product-name">{product.name}</h2>

          {/* Color Selection */}
          {product.colors && product.colors.length > 0 && (
            <div className="product-option">
              <label className="option-label">Color: {selectedColor}</label>
              <div className="color-options">
                {getSortedColors().map((color) => (
                  <button
                    key={color.name}
                    className={`color-option ${selectedColor === color.name ? 'selected' : ''}`}
                    style={{ backgroundColor: color.hex_code }}
                    onClick={() => {
                      setSelectedColor(color.name);
                      setSelectedImage(0); // Reset to first image when changing color
                    }}
                    title={color.name}
                  >
                    {selectedColor === color.name && <div className="selected-indicator"></div>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Size Selection */}
          {product.sizes && product.sizes.length > 0 && (
            <div className="product-option">
              <label className="option-label">Size & Pricing</label>
              <div className="size-table-container">
                <table className="size-table">
                  <thead>
                    <tr>
                      <th>Size</th>
                      <th>Wholesaler Price</th>
                      <th>Retailer Price</th>
                      <th>Unit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {product.sizes.map((size) => (
                      <tr
                        key={size.name}
                        className={`size-row`}
                      >
                        <td className="size-name">{size.size}</td>
                        <td className="wholesale-price">₹{size.wholesale_price || 'N/A'}</td>
                        <td className="retail-price">₹{size.retail_price || 'N/A'}</td>
                        <td className="unit">{size.unit || 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Product Details Section - Full Width Below Images */}
      <div className="product-details-section">
        <div className="detail-item">
          <span className="option-label">SKU: <span className="collapsible-text">{product.sku || 'N/A'}</span></span>
        </div>
        <div className="detail-item">
          <span className="option-label">Category: <span className="collapsible-text">{product.category?.name || 'N/A'}</span></span>
        </div>
        <div className="detail-item">
          <span className="option-label">Brand: <span className="collapsible-text">{product.brand?.name || 'N/A'}</span></span>
        </div>
        <div className="detail-item">
          <span className="option-label">Fabric: <span className="collapsible-text">{product.fabric || 'N/A'}</span></span>
        </div>

        {/* Collapsible Sections */}
        <div className="collapsible-sections">
          <CollapsibleSection
            title="Description"
            content={product.description}
            sectionKey="description"
            isExpanded={expandedSections.description}
            onToggle={toggleSection}
          />

          <CollapsibleSection
            title="Return Policy"
            content={product.return_policy}
            sectionKey="returnPolicy"
            isExpanded={expandedSections.returnPolicy}
            onToggle={toggleSection}
          />
        </div>
      </div>

      {/* Image Modal */}
      {isModalOpen && (
        <div className="image-modal-overlay" onClick={closeImageModal}>
          <div className="image-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={closeImageModal}>
              <FaTimes />
            </button>

            {currentImages.length > 1 && (
              <>
                <button className="modal-nav-btn modal-prev-btn" onClick={prevImage}>
                  <FaChevronLeft />
                </button>
                <button className="modal-nav-btn modal-next-btn" onClick={nextImage}>
                  <FaChevronRight />
                </button>
              </>
            )}

            <div className="modal-image-container">
              <img
                src={`${BASE_URL}${currentImages[modalImageIndex]?.image}`}
                alt={`${product.name} - ${modalImageIndex + 1}`}
                className="modal-image"
                onError={(e) => {
                  console.error('Modal image failed to load:', e.target.src);
                  e.target.style.display = 'none';
                }}
              />
            </div>

            {currentImages.length > 1 && (
              <div className="modal-image-counter">
                {modalImageIndex + 1} / {currentImages.length}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetails; 